import { CommonModule, DOCUMENT, isPlatformServer } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, inject, Input, PLATFORM_ID, Renderer2 } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ImgixService } from '@core-mkt/services/imgix/imgix.service';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowLeft, faArrowRight, faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import { createHtmlEntityDecoder } from '@core-mkt/shared/lib/html-entity-decoder';
import { CareerCenterArticleCardComponent } from '../career-center-article-card/career-center-article-card.component';
import type { CareerCenterArticleCard } from './career-center';
import { CareerCenter } from './career-center';
import { getCareerCenterSchema } from './career-center.schema';

@Component({
  selector: 'career-center',
  templateUrl: './career-center.component.html',
  styleUrls: ['./career-center.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, CareerCenterArticleCardComponent, QueryParamDirective],
})
export class CareerCenterComponent implements OnInit {
  @Input() data: CareerCenter;
  activeIndex = 0;
  selectedCategoryFilter = '';
  categoryList: string[] = [];
  pages = [];
  mobilePageIndex = 0;
  deskPageIndex = 0;
  theme: ThemeInterface;
  careerCenterContent: string | undefined = undefined;
  faArrowRight = faArrowRight;
  faArrowLeft = faArrowLeft;
  faEllipsisH = faEllipsisH;
  originalCareerCenterData: CareerCenterArticleCard[];
  paginationBaseUrl: string;
  hasOtherParams: boolean;

  private document = inject<Document>(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private themeParser = inject(ThemeParserService);
  private redactor = inject(WysiwygRedactorService);
  private imgixService = inject(ImgixService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private renderer = inject(Renderer2);
  private schemaService = inject(SchemaService);

  get careerCenterSideContent(): string | undefined {
    return this.careerCenterContent;
  }

  get careerCenterTitle(): string | undefined {
    return this.data.careerCenterTitle;
  }

  get filterTitle(): string {
    return this.data.filterTitle ? this.data.filterTitle : 'Filter by Category';
  }

  get filterDefaultLabel(): string {
    return this.data.filterDefaultLabel ? this.data.filterDefaultLabel : 'All Resources';
  }

  get careerCenterArticleCards(): CareerCenterArticleCard[] | undefined {
    return this.data.careerCenterArticleCards;
  }

  ngOnInit(): void {
    this.data.careerCenterTitle = this.redactor.bypassSanitizer(this.data.careerCenterTitle);
    if (this.data?.careerCenterSideContent) {
      this.careerCenterContent = this.redactor.bypassSanitizer(this.data.careerCenterSideContent);
    }

    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.data.backgroundColor,
      textColor: this.data.textColor,
      columnCount: this.data.careerCenterArticleCards.length,
      columnDistribution: undefined,
      backgroundImage: undefined,
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
    this.originalCareerCenterData = [...this.data.careerCenterArticleCards];
    this.getCategoryList(this.originalCareerCenterData);
    this.getNumberOfPages([...this.data.careerCenterArticleCards]);

    // Check for page parameter in URL and navigate to that page
    const pageParam = this.route.snapshot.queryParamMap.get('page');
    if (pageParam) {
      const pageNumber = parseInt(pageParam, 10);
      if (pageNumber > 0 && pageNumber <= this.pages.length) {
        this.changePage(pageNumber - 1);
      }
    }
    this.setPaginationBaseUrl();
    this.updateCanonicalUrl();
    this.registerSchema();
  }

  private registerSchema(): void {
    if (!isPlatformServer(this.platformId)) return;
    const decodeEntities = createHtmlEntityDecoder(this.document);
    this.schemaService.register(
      getCareerCenterSchema(this.data.careerCenterArticleCards, this.document.URL, decodeEntities),
    );
  }

  getCategoryList(careerCenterArticleCards: CareerCenterArticleCard[]): void {
    const tempCategoryList: string[] = [];
    careerCenterArticleCards.forEach((element) => {
      if (element.relatedResourcesCategory) {
        element.relatedResourcesCategory.forEach((category) => {
          tempCategoryList.push(category.title);
        });
      } else if (element.careerCenterArticleCards) {
        element.careerCenterArticleCards.forEach((category) => {
          tempCategoryList.push(category.title);
        });
      }
    });
    this.categoryList = [...new Set(tempCategoryList)];
  }

  onCategoryFilterChange(category: string): void {
    this.selectedCategoryFilter = category;

    if (this.selectedCategoryFilter != '') {
      const filteredPages = this.originalCareerCenterData.filter((object) =>
        this.validateExistingResourceCategory(object, category),
      );
      this.getNumberOfPages(filteredPages);
    } else {
      this.clearCategoryFilter();
    }
  }

  validateExistingResourceCategory(categoryObject: CareerCenterArticleCard, category: string): boolean {
    if (categoryObject.relatedResourcesCategory) {
      // TO DO clean up the code when the v3 migration is complete
      return (
        categoryObject.relatedResourcesCategory[0]?.aceCategoryCode === category ||
        categoryObject.relatedResourcesCategory[0]?.title === category
      );
    } else if (categoryObject.careerCenterArticleCards) {
      // TO DO clean up the code when the v3 migration is complete
      return (
        categoryObject.careerCenterArticleCards[0]?.aceCategoryCode === category ||
        categoryObject.careerCenterArticleCards[0]?.title === category
      );
    }
    return false;
  }

  clearCategoryFilter(): void {
    this.selectedCategoryFilter = '';
    this.getNumberOfPages([...this.originalCareerCenterData]);
  }

  getNumberOfPages(careerCenterArticleCards: CareerCenterArticleCard[]): void {
    this.goToFirstPage();
    this.pages = [];
    const articleCards = careerCenterArticleCards;
    while (articleCards.length > 0) {
      this.pages.push(articleCards.splice(0, 10));
    }
  }

  changePage(index: number): void {
    if (index % 2 === 0) {
      this.mobilePageIndex = index;
    }
    if (index % 3 === 0) {
      this.deskPageIndex = index;
      this.mobilePageIndex = index - 1;
    }
    this.activeIndex = index;
    this.updateUrlWithPageParam(index + 1);
    this.updateCanonicalUrl();
  }

  goToNextPage(): void {
    if (this.activeIndex < this.pages.length) {
      this.changePage(this.activeIndex + 1);
    }
  }

  goToFirstPage(): void {
    this.changePage(0);
  }

  goToPreviousPage(): void {
    if (this.activeIndex > 0) {
      this.changePage(this.activeIndex - 1);
    }
  }

  skip(direction: string): void {
    let skipIdx = this.activeIndex + 2;
    if (direction === 'back') {
      skipIdx = this.activeIndex - 2;
    }
    this.changePage(skipIdx);
  }

  scroll(el: HTMLElement): void {
    el.scrollIntoView({
      behavior: 'auto',
      block: 'start',
      inline: 'start',
    });
  }

  private updateUrlWithPageParam(pageNumber: number): void {
    const queryParams = { ...this.route.snapshot.queryParams };

    if (pageNumber > 1) {
      queryParams['page'] = pageNumber.toString();
    } else {
      delete queryParams['page'];
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });
  }

  private updateCanonicalUrl(): void {
    const pageNumber = this.activeIndex + 1;

    // Get the existing canonical URL (set by SEO service)
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const canonicalLink = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonicalLink?.href) {
      const url = new URL(canonicalLink.href);

      // Remove existing page parameter if it exists
      url.searchParams.delete('page');

      // Add page parameter if not on first page
      if (pageNumber > 1) {
        url.searchParams.set('page', pageNumber.toString());
      }

      this.renderer.setAttribute(canonicalLink, 'href', url.toString());
    }
  }

  private setPaginationBaseUrl(): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams['page']; // Remove page param to get clean base

    this.hasOtherParams = Object.keys(queryParams).length > 0;

    if (this.hasOtherParams) {
      const queryString = Object.keys(queryParams)
        .map((key) => `${key}=${queryParams[key] as string}`)
        .join('&');
      this.paginationBaseUrl = this.router.url.split('?')[0] + '?' + queryString;
    } else {
      this.paginationBaseUrl = this.router.url.split('?')[0];
    }
  }

  getPageUrl(pageIndex: number): string {
    if (pageIndex === 0) {
      return this.paginationBaseUrl; // Page 1, no page parameter needed
    }

    const separator = this.hasOtherParams ? '&' : '?';
    return `${this.paginationBaseUrl}${separator}page=${pageIndex + 1}`;
  }
}
